'use client'

import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { cn } from '@/utils/twMerge'
import { PrismicLink } from '@prismicio/react'
import { getFlag, getSwitcherLang, SUPPORTED_LOCALES, getLangPath } from '@/utils/getLangFormat'

type DataObject = Record<string, any>
type LocaleOption = { lang: string; url: string }
type LanguageSwitcherProps = {
  currentLang?: string
  locales?: DataObject
  showListLocalesValue: boolean
  onSetShowListLocalesValue: (value: boolean) => void
}

/**
 * Augment the page-specific locales array with every supported locale, so the
 * switcher renders all four languages even on pages that don't have their own
 * translations (e.g., a US-only blog post). For missing translations we link
 * to the locale's homepage rather than 404'ing or hiding the option entirely.
 *
 * Page-specific URLs from `getLocales(doc)` win when they exist — those are
 * actual translations of the current page, which is what the visitor wants.
 */
function buildSwitcherLocales(pageLocales: DataObject | undefined): LocaleOption[] {
  const fromPage = new Map<string, string>()
  if (Array.isArray(pageLocales)) {
    for (const l of pageLocales) {
      if (l?.lang && l?.url) fromPage.set(l.lang, l.url)
    }
  }
  return SUPPORTED_LOCALES.map((lang) => ({
    lang,
    url: fromPage.get(lang) ?? `/${getLangPath(lang)}`,
  }))
}

const LanguageSwitcher = ({
  currentLang,
  locales,
  showListLocalesValue,
  onSetShowListLocalesValue,
}: LanguageSwitcherProps) => {
  const switcherLocales = buildSwitcherLocales(locales)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (showListLocalesValue) {
      listRef.current?.querySelector<HTMLAnchorElement>('a')?.focus()
    } else if (wasOpenRef.current) {
      triggerRef.current?.focus()
    }
    wasOpenRef.current = showListLocalesValue
  }, [showListLocalesValue])

  return (
    <div className="relative" data-locale-switcher>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={showListLocalesValue}
        aria-controls="localeList"
        className="mr-4 flex items-center"
        onClick={(e) => {
          e.stopPropagation()
          onSetShowListLocalesValue(!showListLocalesValue)
        }}>
        {switcherLocales.map(
          (locale) =>
            currentLang === locale.lang && (
              <p key={locale.lang} className="flex items-center gap-2">
                <Image
                  src={getFlag(currentLang as string)}
                  alt=""
                  aria-hidden="true"
                  width={18}
                  height={18}
                />{' '}
                <span className="text-sm text-white"> {getSwitcherLang(currentLang as string)} </span>
                <Image
                  src="/images/angle-right-white.svg"
                  alt=""
                  aria-hidden="true"
                  width={12}
                  height={12}
                  className={cn(
                    'rotate-90 transition-transform duration-200 ease-out',
                    showListLocalesValue ? 'rotate-[270deg]' : 'rotate-0',
                  )}
                />
              </p>
            ),
        )}
      </button>

      {/* Mounted always: opacity/transform animate open+close without `hidden`/`flex` clash. */}
      <ul
        ref={listRef}
        id="localeList"
        aria-hidden={!showListLocalesValue}
        inert={!showListLocalesValue ? true : undefined}
        className={cn(
          'absolute left-0 top-8 z-30 flex origin-top flex-col overflow-hidden rounded-md bg-primary-white-60',
          'transition-[opacity,transform,visibility] duration-200 ease-out',
          showListLocalesValue
            ? 'visible translate-y-0 scale-100 opacity-100'
            : 'invisible pointer-events-none -translate-y-1 scale-95 opacity-0',
        )}>
        {switcherLocales.map((locale) => (
          <li
            key={locale.lang}
            onClick={() => onSetShowListLocalesValue(false)}
            onKeyDown={() => onSetShowListLocalesValue(false)}
            className={cn(
              'py-2 pl-2.5 pr-8 transition-colors duration-150 ease-out hover:bg-primary-green-20',
              locale.lang === currentLang ? 'bg-secondary-gray-100' : '',
            )}>
            <PrismicLink href={locale.url} className="flex items-center gap-2">
              <Image src={getFlag(locale.lang)} alt="" aria-hidden="true" width={18} height={18} />{' '}
              <span
                className={cn(
                  'text-sm transition-colors duration-150 ease-out',
                  locale.lang === currentLang ? 'text-primary-ink-100' : 'text-primary-ink-85',
                )}>
                {getSwitcherLang(locale.lang)}
              </span>
            </PrismicLink>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default LanguageSwitcher
